import React, { useEffect } from 'react';
import Form, { FormInstance, FormProps } from 'antd/lib/form';
import { isEqualWith } from 'lodash';
import { NamePath } from 'antd/lib/form/interface';

export type ExtendedFormObjectWithId<T extends object> = Record<string, ExtendedFormObject<T>>;

export interface ExtendedFormState {
    isDirty: boolean;
    isInvalid: boolean;
}
// this should calculate and send the states of the form to the parent component
export interface ExtendedFormObject<T extends object = object> {
    form?: FormInstance<T>;
    checkIfDirty?: (currentValues: Partial<T>, specificPropertyOrProperties?: keyof T | keyof T[]) => boolean;
    updateState?: (state: Partial<ExtendedFormState>) => void;
    onFinish?: (values: T) => void;
    areFieldsDirty?: (property: keyof T | keyof T[]) => boolean;
    areFieldsInvalid?: (property: keyof T | keyof T[]) => boolean;
    resetDirty?: () => void;
}

export interface ExtendedFormProps<T extends object>
    extends Omit<FormProps, 'form' | 'validateTrigger' | 'initialValues'> {
    initialValues: T;
    extendedFormObject: ExtendedFormObject<T>;
}

// Helper function to recursively map current values structure with initial values
const mapInitialValuesRecursively = <T extends object>(currentValues: Partial<T>, initialValues: T): T => {
    return Object.keys(currentValues).reduce((acc, key) => {
        const currentValue = currentValues[key];
        const initialValue = initialValues[key];

        if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
            // If current value is an object (but not array), recursively apply the logic
            acc[key] =
                initialValue && typeof initialValue === 'object' && !Array.isArray(initialValue) ?
                    mapInitialValuesRecursively(currentValue, initialValue)
                :   initialValue;
        } else {
            // For non-object values, use initial value or null if it doesn't exist
            acc[key] = initialValue ?? null;
        }

        return acc;
    }, {} as T);
};

export const ExtendedForm = <T extends object>(props: ExtendedFormProps<T>) => {
    const { initialValues, extendedFormObject, ...rest } = props;
    const [form] = Form.useForm();

    const checkIfDirty = (currentValues: Partial<T>, specificPropertyOrProperties?: keyof T | keyof T[]) => {
        const currentValuesWithOnlySpecificPropertyOrProperties =
            specificPropertyOrProperties ?
                Object.keys(currentValues).reduce((acc, key) => {
                    const shouldIncludeKey =
                        typeof specificPropertyOrProperties === 'string' ? specificPropertyOrProperties === key
                        : Array.isArray(specificPropertyOrProperties) ?
                            specificPropertyOrProperties.includes(key as keyof T)
                        :   false;
                    if (shouldIncludeKey) {
                        acc[key] = currentValues[key];
                    }
                    return acc;
                }, {} as T)
            :   currentValues;

        const initialValuesWithOnlySpecificPropertyOrProperties =
            specificPropertyOrProperties ?
                Object.keys(initialValues).reduce((acc, key) => {
                    const shouldIncludeKey =
                        typeof specificPropertyOrProperties === 'string' ? specificPropertyOrProperties === key
                        : Array.isArray(specificPropertyOrProperties) ?
                            specificPropertyOrProperties.includes(key as keyof T)
                        :   false;
                    if (shouldIncludeKey) {
                        acc[key] = initialValues[key];
                    }
                    return acc;
                }, {} as T)
            :   initialValues;

        const initialValuesWithOnlyCurrentValuesKeys = mapInitialValuesRecursively(
            currentValuesWithOnlySpecificPropertyOrProperties,
            initialValuesWithOnlySpecificPropertyOrProperties,
        );

        const isDirty = !isEqualWith(
            initialValuesWithOnlyCurrentValuesKeys,
            currentValuesWithOnlySpecificPropertyOrProperties,
            (a, b) => {
                // make string numbers vs numbers equal
                if (
                    (typeof a === 'string' && typeof b === 'number') ||
                    (typeof a === 'number' && typeof b === 'string')
                ) {
                    return a == b;
                }
                // make nulls equal to empty strings
                if ((a === null && b === '') || (a === '' && b === null)) {
                    return true;
                }

                // make undefined equal to null
                if ((a === undefined && b === null) || (a === null && b === undefined)) {
                    return true;
                }

                return undefined;
            },
        );

        return isDirty;
    };

    extendedFormObject.checkIfDirty = checkIfDirty;
    extendedFormObject.areFieldsDirty = (property: keyof T | keyof T[]) => {
        const propertyOrProperties = typeof property === 'string' ? [property] : property;
        return extendedFormObject.form?.isFieldsTouched(propertyOrProperties as NamePath[]);
    };
    extendedFormObject.areFieldsInvalid = (property: keyof T | keyof T[]) => {
        const propertyOrProperties = typeof property === 'string' ? [property] : property;
        return extendedFormObject.form?.getFieldsError(propertyOrProperties as NamePath[]).length > 0;
    };
    extendedFormObject.resetDirty = () => {
        const currentValues = extendedFormObject.form?.getFieldsValue();
        const isDirty = checkIfDirty(currentValues ?? {});
        extendedFormObject.updateState?.({ isDirty });
    };

    extendedFormObject.form = extendedFormObject.form ?? form;

    // if the initial values are changed, reset the state
    useEffect(() => {
        extendedFormObject.updateState?.({ isDirty: false, isInvalid: false });
    }, [JSON.stringify(initialValues)]);

    const checkIfInvalid = () => {
        extendedFormObject.form
            ?.validateFields()
            .then(() => {
                extendedFormObject.updateState?.({ isInvalid: false });
            })
            .catch((error) => {
                console.error('error', error);
                extendedFormObject.updateState?.({ isInvalid: true });
            });
    };

    return (
        <Form
            {...rest}
            initialValues={initialValues}
            validateTrigger={false}
            onValuesChange={(changedValues, allValues) => {
                rest.onValuesChange?.(changedValues, allValues);
                checkIfInvalid();
                const isDirty = checkIfDirty(allValues);
                extendedFormObject.updateState?.({ isDirty });
            }}
            onFinishFailed={() => {
                extendedFormObject.updateState?.({ isInvalid: true });
            }}
            onFinish={(values) => {
                checkIfInvalid();
                const isDirty = checkIfDirty(values);
                extendedFormObject.updateState?.({ isDirty });
                rest.onFinish?.(values);
            }}
            form={extendedFormObject.form}
        >
            {props.children}
        </Form>
    );
};
